import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import ScoutAvatar from "../../components/scout-request/ScoutAvatar";
import SaveToListPicker from "../../components/agent-radar/profile/SaveToListPicker";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { agentRadarService } from "../../services/agentRadarService";
import { ROLES } from "../../config/roles";
import { RADAR_TOAST_STYLE } from "../../components/agent-radar/radarFormat";

// TP Agent Radar — read-only shared-list snapshot (P3).
// Route /agent/radar/snapshots/:id, flag-gated, opened from the shortlist chat
// card. FROZEN at share time (immutability is structural on the BE — this view
// never reads the live list). Anatomy verbatim from the prototype
// (TP Agent Radar.dc.html snapshot ~L353-378): back link, blue info banner (exact
// copy), list name (18px/700) + "N players", player rows (38px avatar, name
// 13.5px/700, meta, club + crest right). NO actions, nothing editable.

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Snapshot-usability (Marins Findings 2026-08-21): rows link to the frozen
// subject's profile (player → /search/player, coach → /search/coach), the club
// chip links to the club profile when the freeze carried a platform_club_id,
// and the flag renders as an image (never the bare code — rows shared before
// the flag column existed simply show no flag). All four render exactly as
// before when the frozen row carries no target (old snapshots).
const snapshotProfilePath = (entry) => {
  if (entry.ts_coach_id) return `/search/coach/${entry.ts_coach_id}`;
  const pid = entry.ws_player_id || entry.player_id;
  return pid ? `/search/player/${pid}` : null;
};

const SnapshotRow = ({ entry, onNavigate }) => {
  const name = entry.player_name || entry.name || "Player";
  const clubName = entry.club_name || entry.club || "";
  const crest = entry.club_logo_url || entry.club_crest || "";
  const photo = entry.player_image_url || entry.image_url || "";
  const flag = entry.nationality_flag || "";
  const line = [entry.position, entry.age]
    .filter((v) => v !== null && v !== undefined && v !== "")
    .join(" · ");
  const profilePath = snapshotProfilePath(entry);
  const clubPath = entry.platform_club_id ? `/search/club/${entry.platform_club_id}` : null;

  return (
    <div className="flex items-center gap-[11px] p-[10px]">
      {photo ? (
        <img
          src={photo}
          alt=""
          className="w-[38px] h-[38px] rounded-full object-cover flex-shrink-0"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <ScoutAvatar name={name} size={38} />
      )}
      <div className="flex-1 min-w-0">
        <div
          className={`text-[13.5px] font-bold text-[#111827] truncate ${
            profilePath ? "cursor-pointer hover:text-[#2283FF]" : ""
          }`}
          role={profilePath ? "link" : undefined}
          tabIndex={profilePath ? 0 : undefined}
          onClick={profilePath ? () => onNavigate(profilePath) : undefined}
          onKeyDown={
            profilePath
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") onNavigate(profilePath);
                }
              : undefined
          }
        >
          {name}
        </div>
        <div className="flex items-center gap-1.5 mt-px min-w-0">
          {line && <div className="text-[11.5px] text-[#6B7280] truncate">{line}</div>}
          {flag && (
            <img
              src={flag}
              alt={entry.nationality || ""}
              className="h-[11px] w-[15px] rounded-[2px] object-cover flex-shrink-0"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
        </div>
      </div>
      {clubName && (
        <div
          className={`flex items-center gap-1.5 min-w-0 ${
            clubPath ? "cursor-pointer hover:text-[#2283FF]" : ""
          }`}
          role={clubPath ? "link" : undefined}
          tabIndex={clubPath ? 0 : undefined}
          onClick={clubPath ? () => onNavigate(clubPath) : undefined}
          onKeyDown={
            clubPath
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") onNavigate(clubPath);
                }
              : undefined
          }
        >
          {crest ? (
            <img src={crest} alt="" className="w-[15px] h-[15px] rounded-sm object-contain flex-shrink-0" />
          ) : null}
          <div className={`text-[12px] truncate max-w-[120px] ${clubPath ? "text-[#2283FF] font-semibold" : "text-[#6B7280]"}`}>
            {clubName}
          </div>
        </div>
      )}
    </div>
  );
};

// Injects the pop/sheet keyframes SaveToListPicker relies on (no-op if present).
const ensureAnim = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("tp-radar-anim")) return;
  const el = document.createElement("style");
  el.id = "tp-radar-anim";
  el.textContent =
    "@keyframes tp-pop{from{transform:scale(0.96);opacity:0}to{transform:none;opacity:1}}" +
    "@keyframes tp-sheet{from{transform:translateY(48px);opacity:0}to{transform:none;opacity:1}}";
  document.head.appendChild(el);
};

// "Save to my Radar" in the snapshot header (P3, frame 2b). Agent viewers who
// don't own the list copy all snapshot players into their own radar via the
// SHARED save-to-list picker (same mechanic as the profile "Add to Radar"); the
// server dedupes (skipped_count). Non-agent / owner viewers never mount this, so
// the snapshot is byte-for-byte unchanged for them.
const SnapshotSaveButton = ({ snapshotId }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const wrapRef = useRef(null);
  const [lists, setLists] = useState([]);
  const [loadedLists, setLoadedLists] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftListIds, setDraftListIds] = useState([]);
  const [creating, setCreating] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    ensureAnim();
  }, []);

  const loadLists = useCallback(async () => {
    if (loadedLists) return;
    const res = await agentRadarService.getLists();
    if (res.success) setLists(res.data || []);
    setLoadedLists(true);
  }, [loadedLists]);

  const commit = useCallback(
    async (finalIds) => {
      setPickerOpen(false);
      if (!finalIds.length) return;
      const res = await agentRadarService.saveSnapshotToRadar(snapshotId, finalIds);
      if (res.success) {
        setSaved(true);
        const n = res.data?.saved_count;
        toast(
          typeof n === "number" ? `Saved ${n} player${n === 1 ? "" : "s"} to your radar` : "Saved to your radar",
          { position: "bottom-center", style: RADAR_TOAST_STYLE },
        );
      } else {
        toast.error(res.error || "Couldn't save to your radar.");
      }
    },
    [snapshotId],
  );

  const openPicker = () => {
    setDraftListIds([]);
    setHover(false);
    setPickerOpen(true);
    loadLists();
  };

  const toggle = (listId) =>
    setDraftListIds((cur) => (cur.includes(listId) ? cur.filter((x) => x !== listId) : cur.concat(listId)));

  const createList = async (name) => {
    setCreating(true);
    const res = await agentRadarService.createList(name);
    setCreating(false);
    if (!res.success || !res.data?.id) {
      toast.error(res.error || "Couldn't create the list.");
      return false;
    }
    setLists((cur) => cur.concat(res.data));
    setDraftListIds((cur) => cur.concat(res.data.id)); // create + auto-check
    return true;
  };

  const done = () => commit(draftListIds);

  // Desktop: clicking outside the button+popover commits & closes.
  useEffect(() => {
    if (!pickerOpen || isMobile) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) commit(draftListIds);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [pickerOpen, isMobile, draftListIds, commit]);

  const button = saved ? (
    <button
      type="button"
      onClick={openPicker}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="flex items-center gap-[7px] rounded-[9px] px-[15px] py-[9px] text-[13px] font-semibold text-[#111827] border border-[#DDE3EB] whitespace-nowrap flex-shrink-0 cursor-pointer"
      style={{ background: hover ? "#F8FAFC" : "#FFFFFF", minHeight: isMobile ? 44 : undefined }}
    >
      <span className="text-[#16A34A] font-extrabold">✓</span>In your Radar
    </button>
  ) : (
    <button
      type="button"
      onClick={openPicker}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="rounded-[9px] px-[15px] py-[9px] text-[13px] font-semibold text-white border-none whitespace-nowrap flex-shrink-0 cursor-pointer"
      style={{ background: hover ? "#1B6FE0" : "#2283FF", minHeight: isMobile ? 44 : undefined }}
    >
      Save to my Radar
    </button>
  );

  return (
    <div ref={wrapRef} style={{ position: "relative", flexShrink: 0 }}>
      {button}
      {pickerOpen && (
        <SaveToListPicker
          isMobile={isMobile}
          lists={lists}
          draftListIds={draftListIds}
          onToggle={toggle}
          onCreateList={createList}
          creating={creating}
          onDone={done}
        />
      )}
    </div>
  );
};

const RadarSnapshot = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentRole } = useSelector((state) => state.auth);
  const isAgent = currentRole === ROLES.Agent || currentRole === ROLES.AgencyAdmin;
  const [state, setState] = useState({ loading: true, denied: false, data: null });

  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true, denied: false, data: null });
    agentRadarService.getSnapshot(id, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.success) {
          setState({ loading: false, denied: false, data: res.data });
        } else {
          setState({ loading: false, denied: true, data: null });
        }
      })
      // getSnapshot rethrows cancellations (service contract); an aborted mount
      // must not surface as an unhandled rejection.
      .catch(() => {});
    return () => controller.abort();
  }, [id]);

  const backToChat = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/inbox");
  };

  const { loading, denied, data } = state;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EDF0F4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2283FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (denied || !data) {
    return (
      <div className="min-h-screen bg-[#EDF0F4] flex flex-col items-center justify-center text-center px-6">
        <div className="text-[15px] font-bold text-[#111827]">Snapshot not available</div>
        <div className="text-[12.5px] text-[#6B7280] mt-2 max-w-[320px] leading-[1.55]">
          This shared list isn't available to you, or the link has expired.
        </div>
        <button
          type="button"
          onClick={backToChat}
          className="mt-4 bg-white border border-[#DDE3EB] rounded-[9px] px-[14px] py-2 text-[12.5px] font-semibold text-[#111827] hover:bg-[#F8FAFC]"
        >
          ‹ Back to chat
        </button>
      </div>
    );
  }

  const listName = data.list_name || data.name || "Shortlist";
  const entries = Array.isArray(data.entries) ? data.entries : [];
  const count = data.count ?? entries.length;
  const sharedDate = formatDate(data.shared_at || data.shared_on || data.created_at);
  // Save-to-Radar (frame 2b): agent viewers who don't own the list. `is_owner`
  // may be absent on older BE payloads — only hide when it's explicitly true.
  const canSave = isAgent && data.is_owner !== true;

  return (
    <div className="min-h-screen bg-[#EDF0F4]">
      <div className="max-w-[680px] mx-auto px-4 pt-5 pb-14">
        <button
          type="button"
          onClick={backToChat}
          className="text-[#2283FF] text-[12.5px] font-semibold mt-0.5 mb-[14px] block"
        >
          ‹ Back to chat
        </button>

        <div className="bg-[#F0F6FF] border border-[#BFDBFE] rounded-[9px] px-[13px] py-[9px] text-[12px] text-[#1E4B8F] mb-[14px] leading-[1.5]">
          Read-only snapshot · shared {sharedDate} · later changes to the live list are not reflected here.
        </div>

        <div className="flex items-center gap-[10px] mb-3">
          <div className="flex-1 min-w-0 flex items-baseline gap-[9px]">
            <div className="text-[18px] font-bold text-[#111827] truncate">{listName}</div>
            <div className="text-[12px] text-[#9CA3AF] font-semibold whitespace-nowrap flex-shrink-0">
              {count} player{count === 1 ? "" : "s"}
            </div>
          </div>
          {canSave && <SnapshotSaveButton snapshotId={id} />}
        </div>

        <div className="bg-white border border-[#E4E9F0] rounded-[14px] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-1.5">
          {entries.map((entry, i) => (
            <SnapshotRow key={entry.id ?? i} entry={entry} onNavigate={navigate} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default RadarSnapshot;
